<template>
  <Datepicker
    ref="datePickerRef"
    v-model="date"
    locale="ru-RU"
    :disabled="props.disabled"
    :readonly="props.readonly"
    text-input
    :format="DATE_FNS_PATTERN_DATE"
    :max-date="props.maxDate"
    :text-input-options="{
      enabled: true,
      parse: (value: string) => parseISO(value),
      format: (date: Date) => format(date, DATE_FNS_PATTERN_DATE),
    }"
    class="base-date-picker"
    teleport
  >
    <template #time-picker />
    <template #action-preview />
    <template #action-buttons>
      <BaseButton
        size="md-alt"
        with-full
        with-decor
        data-testid="cancel-calendar-date-btn"
        @click="handleCloseMenu"
      >
        Назад
      </BaseButton>
      <BaseButton
        with-full
        with-decor
        variant="primary"
        data-testid="apply-calendar-date-btn"
        @click="handleSelectDate"
      >
        Применить
      </BaseButton>
    </template>
    <template #dp-input="{ onInput, onEnter }">
      <BaseInput
        type="text"
        :size="props.size"
        :placeholder="props.placeholder"
        inputmode="decimal"
        :readonly="props.readonly"
        :disabled="props.disabled"
        :with-full="props.withFull"
        :tabindex="props.tabindex"
        :mask="MASK_OF_DATE"
        :model-value="date && isValid(date) ? format(date, DATE_FNS_PATTERN_DATE) : ''"
        :variant="props.variant"
        :with-clear="props.withClear"
        :prepend-icon="props.prependIcon"
        :prepend-icon-color="props.prependIconColor"
        :append-icon="props.appendIcon"
        :append-icon-color="props.appendIconColor"
        :error-message="props.errorMessage"
        data-testid="datepicker-input"
        @keydown.enter="onEnter"
        @update:model-value="
          handleChangeData($event as string, onInput, onEnter)
        "
        @cleared="handleClear"
        @click-append-icon="emit('clickAppendIcon', props.name)"
      />
    </template>
  </Datepicker>
</template>

<script setup lang="ts">
import Datepicker from '@vuepic/vue-datepicker'
import { format, isValid, parse, parseISO } from 'date-fns'
import { ref, watch } from 'vue'

import { DATE_FNS_PATTERN_DATE, DATE_FNS_PATTERN_ISO_DAY } from '~/shared/lib/date'
import { MASK_OF_DATE } from '../../const/masks'
import BaseButton from '../button/index.vue'
import BaseInput from '../input/index.vue'

interface Props {
  modelValue?: Date | string | null
  name?: string
  maxDate?: Date
  tabindex?: number
  size?: 'md' | 'lg'
  variant?: 'light'
  withFull?: boolean
  withPrependIcon?: boolean
  withAppendIcon?: boolean
  placeholder?: string
  prependIcon?: string
  prependIconColor?: string
  appendIcon?: string
  appendIconColor?: string
  withClear?: boolean
  alwaysShowClear?: boolean
  disabled?: boolean
  readonly?: boolean
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  withAppendIcon: true,
  prependIconColor: 'var(--color-foreground-primary)',
  appendIcon: 'vuesax:calendar-linear',
  appendIconColor: 'var(--color-foreground-primary)',
  placeholder: 'дд.мм.гггг',
})
const emit = defineEmits<{
  (e: 'update:modelValue', val: string | null): void
  (e: 'cleared', name?: string): void
  (e: 'clickAppendIcon', name?: string): void
}>()

const datePickerRef = ref<InstanceType<typeof Datepicker>>()
const date = ref<Date | null>(null)

async function handleSelectDate() {
  datePickerRef.value?.selectDate()
  await nextTick()
  await nextTick()
  handleCloseMenu()
}

function handleCloseMenu() {
  datePickerRef.value?.closeMenu()
}

function handleClear() {
  date.value = null
  emit('cleared', props.name)

  setTimeout(() => {
    handleCloseMenu()
  })
}

function handleChangeData(
  value: string,
  onInput: (value: string) => void,
  onEnter: (event: KeyboardEvent) => void,
) {
  const event = new KeyboardEvent('input', {
    bubbles: true,
    cancelable: true,
  })

  Object.defineProperty(event, 'target', {
    value: { value },
    writable: false,
  })

  onInput(value)

  const parsedDate = parse(value, 'dd.MM.yyyy', new Date())
  if (isValid(parsedDate) && value.length === 10) {
    onEnter(event)
  }
}

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      date.value = null
      return
    }

    const parsedDate = typeof value === 'string' ? parseISO(value) : value

    date.value = isValid(parsedDate) ? parsedDate : null
  },
  { immediate: true },
)

watch(date, (value) => {
  if (!value) {
    emit('update:modelValue', null)
    return
  }

  const newValue = format(value, DATE_FNS_PATTERN_ISO_DAY)

  if (newValue !== props.modelValue) {
    emit('update:modelValue', newValue)
  }
})
</script>

<style lang="scss" scoped>
.base-date-picker:deep(*) {
  .dp {
    &__menu {
      overflow: hidden;
    }
  }
}
</style>

<style lang="scss">
.dp {
  &__menu {
    overflow: hidden;
    z-index: 9999 !important;
  }

  &__calendar_header {
    padding: 0 20px;
    background: var(--color-surface-bg);

    &_item {
      color: var(--dp-secondary-color);
      padding: 5px;
    }

    &_separator {
      display: none;

      + .dp__calendar {
        padding: 0 20px;
      }
    }
  }

  &__outer_menu_wrap {
    font-weight: 600;
  }

  &__action {
    &_row {
      display: block;
    }

    &_buttons {
      margin-inline-start: unset;
      width: 100%;
      display: flex;
      gap: 8px;
    }

    &_button {
      height: 40px;
      flex: 1;
      display: flex;
      justify-content: center;
    }
  }

  &__today {
    border: 1px solid var(--color-surface-danger);
  }

  &__range_end,
  &__range_start,
  &__active_date {
    background-color: var(--color-surface-danger);
  }

  &__cell_auto_range_start,
  &__cell_auto_range_end,
  &__date_hover_start:hover,
  &__date_hover_end:hover,
  &__range_start,
  &__range_end {
    border-radius: var(--dp-cell-border-radius);
  }

  &__overlay {
    border-radius: var(--dp-border-radius);
  }

  &__month_year_wrap {
    gap: 8px;
  }

  &__btn.dp__month_year_select,
  &__inner_nav {
    background-color: var(--color-surface-placeholder);
  }

  &--header-wrap {
    padding: 20px 20px 0;
    background: var(--color-surface-bg);
  }

  &--clear-btn {
    display: none;
  }
}
</style>
